import React, { useLayoutEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './index.scss';
import { COMPARED_PAGE_SIZE, LIST_TYPES, SECTION_TEXT } from '../../../../constants';
import { buildProjectComparisonPayload } from '../../../../utils/buildProjectComparisonPayload';
import ComparedRow from './children/ComparedRow';
import SVGIcons from '../../../../../../../../../../Components/SVGIcons';

const SCROLL_ID = 'trend-compared-scroll';

const ComparedTable = (props) => {
    const api = props.fetchProjectComparisonApi || {};
    const { searchText } = props;
    // Leaders exactly as the API sends them — rendered as-is, no reshaping.
    const leaders = api.data || [];

    // When a match-mode change cuts the list back, the scroll position would be
    // past the end of the shorter list — which InfiniteScroll reads as "at the
    // bottom" and loads straight away, undoing the cut. Jump back to the top
    // before paint instead, so the next page loads only when scrolled to.
    const previousCount = useRef(leaders.length);
    useLayoutEffect(() => {
        const body = document.getElementById(SCROLL_ID);
        if (body && leaders.length && leaders.length < previousCount.current) body.scrollTop = 0;
        previousCount.current = leaders.length;
    }, [leaders.length]);

    // Next page of this list; `from` is how many leaders it already has loaded.
    const fetchPage = (from) => {
        props.getProjectComparison(buildProjectComparisonPayload({
            benchmarkFiltersAnalysis: props.benchmarkFiltersAnalysis,
            listType: LIST_TYPES.COMPARED,
            from,
            size: COMPARED_PAGE_SIZE,
            searchText,
        }));
    }


    const handleLoadMore = () => {
        if (api.isProcessing || api.isLoadingMore || !api.hasMore) return;
        fetchPage(leaders.length);
    }

    // Nobody was matched to an earlier 360 at all: the section has nothing to
    // say, so it stays away entirely. A search that happens to match none of
    // them is a different thing — that keeps its header and explains itself.
    if (!api.isProcessing && !api.error && !searchText && !leaders.length) return null;
    // The first load is covered by ComparisonView's graph loader; this shows
    // while a search reloads the list.
    const renderBody = () => {
        if (api.isProcessing) return <div className='trend-table__empty'>{SECTION_TEXT.compared.loading}</div>;
        if (api.error && !leaders.length) return <div className='trend-table__empty'>{SECTION_TEXT.compared.error}</div>;
        if (!leaders.length) return <div className='trend-table__empty'>{SECTION_TEXT.compared.empty}</div>;
        return (
            <div className='trend-table__body' id={SCROLL_ID}>
                <InfiniteScroll
                    dataLength={leaders.length}
                    next={handleLoadMore}
                    hasMore={!!api.hasMore}
                    scrollableTarget={SCROLL_ID}
                    scrollThreshold={0.9}
                    loader={<div className='trend-table__loader'>Loading more participants…</div>}
                >
                    {/* Leaders keep their object identity across pages (the
                        reducer appends), so loading more mounts only the new rows. */}
                    {leaders.map((leader) => (
                        <ComparedRow key={leader.selfEmail} leader={leader} />
                    ))}
                </InfiniteScroll>
            </div>
        );
    };

    return (
        <div className='trend-table trend-compared'>
            <div className='trend-table__head'>
                <span className='trend-table__head-title'>
                    <span className='trend-table__head-icon'><SVGIcons type={"SVG-tick"} /></span>
                    {SECTION_TEXT.compared.title}
                </span>
                {/* How many this list holds out of everyone in scope — not how
                    many are loaded, so paging leaves it alone. A search narrows
                    the first number only: "2 of 7", never "2 of 2". */}
                {!api.isProcessing && (
                    <span className='trend-table__badge'>{props.listTotal} of {props.totalCount}</span>
                )}
            </div>

            {renderBody()}
        </div>
    );
};

export default ComparedTable;
